import { AlertTriangle, CheckCircle2, Info, MousePointerClick, ShieldAlert, Users } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { usePermissionCatalog } from '../api'
import { findPermission, isSensitive, prerequisitesOf, type PermissionRef } from '../permissions'
import type { Role } from '../types'
import { useAccessLookups } from '../useLookups'
import { permissionLabel } from '../ui'
import { AvatarStack } from './AccessBits'
import { ModuleIcon } from './ModuleIcon'

type Props = {
  code: string | null
  granted: ReadonlySet<string>
  role: Role
  readOnly?: boolean
  onGrant: (code: string) => void
  onViewUsers: () => void
}

function restrictionsOf(ref: PermissionRef): string[] {
  const keys: string[] = []
  if (ref.action === 'approve') keys.push('restrictionApproveOwn', 'restrictionApproveRejected')
  if (ref.action === 'delete') keys.push('restrictionDelete')
  if (ref.action === 'export') keys.push('restrictionExport')
  if (isSensitive(ref)) keys.push('restrictionSensitive')
  return keys
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="space-y-1.5 border-t pt-4">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
    {children}
  </div>
)

export function PermissionDetailPanel({ code, granted, role, readOnly, onGrant, onViewUsers }: Props) {
  const { t } = useTranslation()
  const catalogData = usePermissionCatalog().data
  const catalog = catalogData?.modules ?? []
  const { userNames, targetNames } = useAccessLookups()
  const ref = code ? findPermission(code, catalog) : null
  const title = code ? catalogData?.titleByCode[code] : undefined
  const description = code ? catalogData?.descriptionByCode[code] : undefined

  if (!ref && !code) {
    return (
      <aside className="flex flex-col items-center justify-center gap-2 rounded-2xl border bg-card p-6 text-center text-sm text-muted-foreground">
        <MousePointerClick className="size-5" />
        {t('access.panel.empty')}
      </aside>
    )
  }

  if (!ref && code) {
    const on = granted.has(code)
    return (
      <aside className="space-y-4 rounded-2xl border bg-card p-4 xl:sticky xl:top-24 xl:self-start">
        <h3 className="font-semibold leading-tight">{title || description || code}</h3>
        <Badge tone={on ? 'success' : 'neutral'}>{on ? t('access.panel.granted') : t('access.panel.notGranted')}</Badge>
        <code className="block rounded-md bg-muted px-2 py-1 font-mono text-xs text-muted-foreground">{code}</code>
        {description && description !== title ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </aside>
    )
  }

  if (!ref) return null

  const resource = t(`access.resources.${ref.resource.key}`)
  const prerequisites = prerequisitesOf(ref.code, catalog)
  const restrictions = restrictionsOf(ref)
  const users = userNames(role)
  const targets = targetNames(role)
  const on = granted.has(ref.code)

  return (
    <aside className="space-y-4 rounded-2xl border bg-card p-4 xl:sticky xl:top-24 xl:self-start">
      <div className="flex gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue sm:size-10 sm:rounded-xl">
          <ModuleIcon module={ref.module.key} className="size-4 sm:size-5" />
        </span>
        <div className="min-w-0 space-y-1">
          <h3 className="font-semibold leading-tight">{title || permissionLabel(t, ref.code, catalogData ?? catalog)}</h3>
          <div className="flex flex-wrap gap-1">
            <Badge tone={on ? 'success' : 'neutral'}>{on ? t('access.panel.granted') : t('access.panel.notGranted')}</Badge>
            {isSensitive(ref) ? (
              <Badge tone="danger">
                <ShieldAlert className="size-3" />
                {t('access.panel.sensitive')}
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
      <code className="block rounded-md bg-muted px-2 py-1 font-mono text-xs text-muted-foreground">{ref.code}</code>
      <p className="text-sm">{description || t(`access.actionHelp.${ref.action}`, { resource })}</p>

      <Section title={t('access.scope.label')}>
        <p className="text-sm font-medium">{t(`access.scope.${role.scope}`)}</p>
        <p className="text-xs text-muted-foreground">{targets.length ? targets.join(', ') : t(`access.scope.${role.scope}Hint`)}</p>
      </Section>

      <Section title={t('access.panel.prerequisites')}>
        {prerequisites.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('access.panel.noPrerequisites')}</p>
        ) : (
          <ul className="space-y-1.5">
            {prerequisites.map((p) => {
              const met = granted.has(p)
              return (
                <li key={p} className="flex items-center gap-2 text-sm">
                  {met ? <CheckCircle2 className="size-4 text-success" /> : <AlertTriangle className="size-4 text-warning" />}
                  <span className="flex-1">{permissionLabel(t, p, catalogData ?? catalog)}</span>
                  {!met && on && !readOnly ? (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onGrant(p)}>
                      {t('access.panel.grant')}
                    </Button>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
        {on && prerequisites.some((p) => !granted.has(p)) ? <p className="text-xs text-warning">{t('access.panel.prerequisiteMissing')}</p> : null}
      </Section>

      <Section title={t('access.panel.restrictions')}>
        {restrictions.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('access.panel.noRestrictions')}</p>
        ) : (
          <ul className="list-disc space-y-1 pl-4 text-sm">
            {restrictions.map((k) => (
              <li key={k}>{t(`access.panel.${k}`)}</li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={t('access.panel.affectedUsers')}>
        <div className="flex items-center justify-between gap-2">
          <AvatarStack names={users} max={4} />
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={onViewUsers}>
            <Users />
            {t('access.panel.viewUsers')}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t('access.panel.affectedHint', { count: users.length })}</p>
      </Section>

      <div className="flex gap-2 rounded-xl border border-brand-blue/20 bg-brand-blue/[0.06] p-3 text-xs">
        <Info className="mt-0.5 size-3.5 shrink-0 text-brand-blue" />
        <span>{t('access.panel.note', { action: t(`access.actions.${ref.action}`) })}</span>
      </div>
    </aside>
  )
}
